import { FloatingLabel } from "@/components/FloatingLabel";
import CitySelect from "@/components/hlidaci-pes/CitySelect";
import { MultiSelect } from "@/components/modals/MultiSelect";
import { DatePicker } from "@/components/portfolio/DatePicker";
import { Button } from "@/components/ui/Button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { PanelGrid } from "@/components/ui/Panel";
import { Input } from "@/components/ui/Input";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import {
  accessoriesOptions,
  comercialOptions,
  estateConditionOptions,
  flatOptions,
  landOptions,
  otherOptions,
  ownershipOptions,
} from "@/utils/EstateForm";
import { useEffect, useState } from "react";

type CreateNewEstateProps = {
  embedded?: boolean;
};

export function CreateNewEstate({ embedded = false }: CreateNewEstateProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      addProperty: "Přidat nemovitost",
      editProperty: "Upravit nemovitost",
      description: "Vyberte pole ze seznamu",
      propertyName: "Mé pojmenování nemovitosti",
      estateType: "Typ nemovitosti",
      flats: "Byty",
      houses: "Domy",
      land: "Pozemky",
      commercial: "Komerční",
      other: "Ostatní",
      city: "Město",
      configureDetails: "Detaily",
      back: "Krok zpět",
      acquisitionDate: "Datum pořízení",
      disposition: "Dispozice",
      landType: "Typ pozemku",
      commercialType: "Typ komerční",
      otherType: "Typ ostatní",
      ownership: "Vlastnictví",
      condition: "Stav nemovitosti",
      accessories: "Příslušenství",
      usableArea: "Užitná plocha",
      purchasePrice: "Pořizovací cena",
      note: "Poznámka",
      setTenant: "Nájemce",
      setManager: "Správce",
      tenantInfo: "Informace o nájemníkovi",
      managerInfo: "Informace o správci",
      name: "Jméno",
      phone: "Telefon",
      email: "Email",
      clearAndBack: "Vymazat a jít zpět",
      continue: "Pokračovat",
      discard: "Zahodit",
      save: "Uložit",
    },
    en: {
      addProperty: "Add property",
      editProperty: "Edit property",
      description: "Select fields from the list",
      propertyName: "My property name",
      estateType: "Property type",
      flats: "Flats",
      houses: "Houses",
      land: "Land",
      commercial: "Commercial",
      other: "Other",
      city: "City",
      configureDetails: "Complete details",
      back: "Go back",
      acquisitionDate: "Acquisition date",
      disposition: "Disposition",
      landType: "Land type",
      commercialType: "Commercial type",
      otherType: "Other type",
      ownership: "Ownership",
      condition: "Property condition",
      accessories: "Amenities",
      usableArea: "Usable area",
      purchasePrice: "Acquisition price",
      note: "Note",
      setTenant: "Tenant",
      setManager: "Manager",
      tenantInfo: "Tenant information",
      managerInfo: "Manager information",
      name: "Name",
      phone: "Phone",
      email: "Email",
      clearAndBack: "Clear and go back",
      continue: "Continue",
      discard: "Discard",
      save: "Save",
    },
  }[language];
  const { isLoading } = useAppContext();
  const { stepNumber, setStepNumber, selectedItem } = useMenuItemsContext();

  const {
    formDataMyEstate,
    setFormDataMyEstate,
    handleDiscardChanges,
    resetDetailSearch,
    saveMyEstate,
    selectedEstate,
  } = useEstateContext();

  const isFormValid =
    formDataMyEstate.name &&
    formDataMyEstate.estateType &&
    (formDataMyEstate.estateType === "Domy" || formDataMyEstate.flatCategory) &&
    formDataMyEstate.exactAddress?.trim();

  const isNew =
    embedded ||
    selectedItem?.name === "Přidat nemovitost" ||
    selectedItem?.name === copy.addProperty;
  const [showDetails, setShowDetails] = useState(false);
  const estateTypeOptions = [
    { value: "Byty", label: copy.flats },
    { value: "Domy", label: copy.houses },
    { value: "Pozemky", label: copy.land },
    { value: "Komerční", label: copy.commercial },
    { value: "Ostatní", label: copy.other },
  ];
  const selectedEstateTypeLabel =
    estateTypeOptions.find(({ value }) => value === formDataMyEstate.estateType)
      ?.label ?? "";
  const selectedOption = (value?: string | null) => (value ? [value] : []);
  const flatCategoryConfig =
    formDataMyEstate.estateType === "Byty"
      ? { label: copy.disposition, options: flatOptions }
      : formDataMyEstate.estateType === "Pozemky"
        ? { label: copy.landType, options: landOptions }
        : formDataMyEstate.estateType === "Komerční"
          ? { label: copy.commercialType, options: comercialOptions }
          : formDataMyEstate.estateType === "Ostatní"
            ? { label: copy.otherType, options: otherOptions }
            : null;

  const flatCategoryField = flatCategoryConfig ? (
    <div className="app-estate-form-field">
      <FloatingLabel
        inFlow
        htmlFor="flatCategory"
        value={formDataMyEstate.flatCategory}
      >
        {flatCategoryConfig.label}
      </FloatingLabel>
      <MultiSelect
        id="flatCategory"
        options={flatCategoryConfig.options}
        selected={selectedOption(formDataMyEstate.flatCategory)}
        setSelected={(values) => {
          const value = values.at(-1);

          if (!value) {
            return;
          }

          setFormDataMyEstate({
            ...formDataMyEstate,
            doResetWatchDog: !isNew,
            flatCategory: value,
          });
        }}
        avoidCollisions
        selectionMode="single"
        showSearch={false}
        placeholder={flatCategoryConfig.label}
        className="mb-0 w-full"
      />
    </div>
  ) : null;

  useEffect(() => {
    if (!isNew && selectedEstate) {
      setFormDataMyEstate({
        ...selectedEstate,
        isNew: false,
        doResetWatchDog: false,
      });
    }
  }, [isNew, selectedEstate]);

  const form = (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        if (stepNumber !== 1) {
          setStepNumber(1);
          return;
        }

        if (showDetails) {
          setShowDetails(false);
          return;
        }

        setShowDetails(false);
        saveMyEstate();
      }}
      className={
        embedded ? "app-estate-form" : "app-estate-form app-estate-form--dialog"
      }
    >
      {embedded ? (
        <header className="text-left">
          <h3 className="app-card-title text-base leading-tight">
            {copy.addProperty}
          </h3>
          <p className="app-section-description mt-1">{copy.description}</p>
        </header>
      ) : (
        <DialogHeader>
          <DialogTitle>
            {isNew ? copy.addProperty : copy.editProperty}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
      )}

      {stepNumber === 1 ? (
        <PanelGrid className="app-estate-form-fields">
          {!showDetails ? (
            <>
              <div
                data-tour="portfolio-name-field"
                className="app-estate-form-field"
              >
                {/* Label */}
                <FloatingLabel
                  inFlow
                  htmlFor="name"
                  value={formDataMyEstate.name}
                >
                  {copy.propertyName}
                </FloatingLabel>
                {/* Input */}
                <Input
                  id="name"
                  type="text"
                  placeholder={copy.propertyName}
                  value={formDataMyEstate.name ?? ""}
                  required
                  onChange={(e) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      name: e.target.value,
                    })
                  }
                  className="w-full"
                  min={0}
                />
              </div>

              <div
                data-tour="portfolio-estate-type-field"
                className="app-estate-form-field"
              >
                <FloatingLabel
                  inFlow
                  htmlFor="estateType"
                  value={selectedEstateTypeLabel}
                >
                  {copy.estateType}
                </FloatingLabel>
                <MultiSelect
                  id="estateType"
                  options={estateTypeOptions.map(({ label }) => label)}
                  selected={
                    selectedEstateTypeLabel ? [selectedEstateTypeLabel] : []
                  }
                  setSelected={(values) => {
                    const selectedLabel = values.at(-1);
                    const selectedOption = estateTypeOptions.find(
                      ({ label }) => label === selectedLabel,
                    );

                    if (!selectedOption) {
                      return;
                    }

                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      doResetWatchDog: !isNew,
                      estateType: selectedOption.value,
                      flatCategory:
                        selectedOption.value === "Domy"
                          ? ""
                          : selectedOption.value === formDataMyEstate.estateType
                            ? formDataMyEstate.flatCategory
                            : "",
                    });
                  }}
                  avoidCollisions
                  selectionMode="single"
                  showSearch={false}
                  placeholder={copy.estateType}
                  className="mb-0 w-full"
                />
              </div>
              <div
                data-tour="portfolio-city-field"
                className="app-estate-form-field"
              >
                <FloatingLabel
                  inFlow
                  htmlFor="exactAddress"
                  value={formDataMyEstate.exactAddress}
                >
                  {copy.city}
                </FloatingLabel>
                <CitySelect
                  isNew={isNew}
                  formData={formDataMyEstate}
                  setFormData={setFormDataMyEstate}
                />
              </div>
              {flatCategoryField}
            </>
          ) : null}
          {showDetails ? (
            <>
              <div className="app-estate-form-field">
                {/* Floating label */}
                <FloatingLabel
                  inFlow
                  htmlFor="dateFrom"
                  value={formDataMyEstate.dateFrom}
                >
                  {copy.acquisitionDate}
                </FloatingLabel>

                <DatePicker
                  id={`dateFrom`}
                  label={copy.acquisitionDate}
                  useDays={true}
                  value={formDataMyEstate.dateFrom}
                  onChange={(date) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      dateFrom: date ? date : "",
                    })
                  }
                />
              </div>

              <div className="app-estate-form-field">
                <FloatingLabel
                  inFlow
                  htmlFor="ownerShip"
                  value={formDataMyEstate.ownerShip}
                >
                  {copy.ownership}
                </FloatingLabel>

                <MultiSelect
                  id="ownerShip"
                  options={ownershipOptions}
                  selected={selectedOption(formDataMyEstate.ownerShip)}
                  setSelected={(values) => {
                    const value = values.at(-1);

                    if (!value) {
                      return;
                    }

                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      doResetWatchDog: !isNew,
                      ownerShip: value,
                    });
                  }}
                  avoidCollisions
                  selectionMode="single"
                  showSearch={false}
                  placeholder={copy.ownership}
                  className="mb-0 w-full"
                />
              </div>
              <div className="app-estate-form-field">
                <FloatingLabel
                  inFlow
                  htmlFor="estateCondition"
                  value={formDataMyEstate.estateCondition}
                >
                  {copy.condition}
                </FloatingLabel>
                <MultiSelect
                  id="estateCondition"
                  options={estateConditionOptions}
                  selected={selectedOption(formDataMyEstate.estateCondition)}
                  setSelected={(values) => {
                    const value = values.at(-1);

                    if (!value) {
                      return;
                    }

                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      doResetWatchDog: !isNew,
                      estateCondition: value,
                    });
                  }}
                  avoidCollisions
                  selectionMode="single"
                  showSearch={false}
                  placeholder={copy.condition}
                  className="mb-0 w-full"
                />
              </div>

              <div className="app-estate-form-field">
                <FloatingLabel
                  inFlow
                  htmlFor="accessories"
                  value={formDataMyEstate.accessories?.toString()}
                >
                  {copy.accessories}
                </FloatingLabel>
                <MultiSelect
                  id="accessories"
                  options={accessoriesOptions}
                  selected={
                    Array.isArray(formDataMyEstate.accessories)
                      ? formDataMyEstate.accessories
                      : formDataMyEstate.accessories
                        ? (
                            formDataMyEstate.accessories as unknown as string
                          ).split(",")
                        : []
                  }
                  setSelected={(values: string[]) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      doResetWatchDog: !isNew,
                      accessories: values,
                    })
                  }
                  avoidCollisions
                  className="mb-0 w-full"
                  placeholder={copy.accessories}
                />
              </div>

              <div className="app-estate-form-field">
                <FloatingLabel
                  inFlow
                  htmlFor="usableArea"
                  value={formDataMyEstate.usableArea}
                >
                  {copy.usableArea}
                </FloatingLabel>
                <Input
                  id="usableArea"
                  type="number"
                  placeholder={copy.usableArea}
                  value={formDataMyEstate.usableArea ?? ""}
                  onChange={(e) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      doResetWatchDog: !isNew,
                      usableArea:
                        e.target.value === "" ? 0 : Number(e.target.value),
                    })
                  }
                  className="w-full"
                  min={0}
                />
              </div>

              <div className="app-estate-form-field">
                {/* Label */}
                <FloatingLabel
                  inFlow
                  htmlFor="purchasePrice"
                  value={formDataMyEstate.purchasePrice}
                >
                  {copy.purchasePrice}
                </FloatingLabel>
                {/* Input */}
                <Input
                  id="purchasePrice"
                  type="number"
                  placeholder={copy.purchasePrice}
                  value={formDataMyEstate.purchasePrice ?? ""}
                  onChange={(e) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      purchasePrice:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className="w-full"
                  min={0}
                />
              </div>
              <div className="app-estate-form-field">
                <FloatingLabel
                  inFlow
                  htmlFor="note"
                  value={formDataMyEstate.note}
                >
                  {copy.note}
                </FloatingLabel>
                <Input
                  id="note"
                  type="text"
                  placeholder={copy.note}
                  value={formDataMyEstate.note ?? ""}
                  onChange={(e) =>
                    setFormDataMyEstate({
                      ...formDataMyEstate,
                      note: e.target.value,
                    })
                  }
                  className="w-full"
                  min={0}
                />
              </div>
            </>
          ) : null}

          {!showDetails ? (
            <div className="app-estate-form-navigation">
              <Button
                data-tour="portfolio-details-button"
                type="button"
                variant="secondary"
                onClick={() => setShowDetails(true)}
              >
                {copy.configureDetails}
              </Button>
              <Button
                data-tour="portfolio-tenant-button"
                type="button"
                variant="secondary"
                onClick={() => setStepNumber(2)}
              >
                {copy.setTenant}
              </Button>
              <Button
                data-tour="portfolio-manager-button"
                type="button"
                variant="secondary"
                onClick={() => setStepNumber(3)}
              >
                {copy.setManager}
              </Button>
            </div>
          ) : null}
        </PanelGrid>
      ) : stepNumber === 2 ? (
        <PanelGrid className="app-estate-form-fields">
          {embedded ? (
            <p className="text-sm text-foreground">{copy.tenantInfo}</p>
          ) : (
            <DialogDescription>{copy.tenantInfo}</DialogDescription>
          )}
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="tenantName"
              value={formDataMyEstate.tenantName}
            >
              {copy.name}
            </FloatingLabel>
            <Input
              id="tenantName"
              type="text"
              placeholder={copy.name}
              value={formDataMyEstate.tenantName ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  tenantName: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="tenantPhone"
              value={formDataMyEstate.tenantPhone}
            >
              {copy.phone}
            </FloatingLabel>
            <Input
              id="tenantPhone"
              type="text"
              placeholder={copy.phone}
              value={formDataMyEstate.tenantPhone ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  tenantPhone: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="tenantEmail"
              value={formDataMyEstate.tenantEmail}
            >
              {copy.email}
            </FloatingLabel>
            <Input
              id="tenantEmail"
              type="text"
              placeholder={copy.email}
              value={formDataMyEstate.tenantEmail ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  tenantEmail: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="tenantNote"
              value={formDataMyEstate.tenantNote}
            >
              {copy.note}
            </FloatingLabel>
            <Input
              id="tenantNote"
              type="text"
              placeholder={copy.note}
              value={formDataMyEstate.tenantNote ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  tenantNote: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
        </PanelGrid>
      ) : stepNumber === 3 ? (
        <PanelGrid className="app-estate-form-fields">
          {embedded ? (
            <p className="text-sm text-foreground">{copy.managerInfo}</p>
          ) : (
            <DialogDescription>{copy.managerInfo}</DialogDescription>
          )}
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="managerName"
              value={formDataMyEstate.managerName}
            >
              {copy.name}
            </FloatingLabel>
            <Input
              id="managerName"
              type="text"
              placeholder={copy.name}
              value={formDataMyEstate.managerName ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  managerName: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="managerPhone"
              value={formDataMyEstate.managerPhone}
            >
              {copy.phone}
            </FloatingLabel>
            <Input
              id="managerPhone"
              type="text"
              placeholder={copy.phone}
              value={formDataMyEstate.managerPhone ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  managerPhone: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="managerEmail"
              value={formDataMyEstate.managerEmail}
            >
              {copy.email}
            </FloatingLabel>
            <Input
              id="managerEmail"
              type="text"
              placeholder={copy.email}
              value={formDataMyEstate.managerEmail ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  managerEmail: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
          <div className="app-estate-form-field">
            <FloatingLabel
              inFlow
              htmlFor="managerNote"
              value={formDataMyEstate.managerNote}
            >
              {copy.note}
            </FloatingLabel>
            <Input
              id="managerNote"
              type="text"
              placeholder={copy.note}
              value={formDataMyEstate.managerNote ?? ""}
              onChange={(e) =>
                setFormDataMyEstate({
                  ...formDataMyEstate,
                  managerNote: e.target.value,
                })
              }
              className="w-full"
              min={0}
            />
          </div>
        </PanelGrid>
      ) : null}

      {embedded ? (
        <div className="app-estate-form-actions">{renderFormActions()}</div>
      ) : (
        <DialogFooter className="app-estate-form-actions">
          {renderFormActions()}
        </DialogFooter>
      )}
    </form>
  );

  function renderFormActions() {
    return (
      <>
        {stepNumber === 2 || stepNumber === 3 ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => resetDetailSearch()}
            >
              {copy.clearAndBack}
            </Button>
            <Button
              type="button"
              variant="default"
              className="relative"
              onClick={(e) => {
                e.preventDefault();
                setStepNumber(1);
              }}
            >
              {copy.continue}
            </Button>
          </>
        ) : showDetails ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDetails(false)}
            >
              {copy.back}
            </Button>
            <Button
              type="button"
              variant="default"
              className="relative"
              onClick={() => setShowDetails(false)}
            >
              {copy.continue}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleDiscardChanges();
                setShowDetails(false);
              }}
            >
              {copy.discard}
            </Button>

            <Button
              type="submit"
              disabled={isLoading || !isFormValid}
              variant="default"
              className="relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{copy.save}</span>
                  <span className="dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </span>
                </>
              ) : (
                copy.save
              )}
            </Button>
          </>
        )}
      </>
    );
  }

  if (embedded) {
    return (
      <div
        data-tour="portfolio-create-form"
        className="app-estate-form-embedded"
      >
        {form}
      </div>
    );
  }

  return (
    <DialogContent className="app-dialog-workspace app-estate-dialog">
      {form}
    </DialogContent>
  );
}

export function NewEstateDialog() {
  return <CreateNewEstate embedded />;
}
